import { Injectable } from '@angular/core';

import { UTM, UTM_MODEL_KEYS } from '@shared/interfaces/utm';
import { Router } from '@angular/router';
import { Deal, ordersPerDayValues, waiterQuantityValues, waiterTablesValues } from '@infra/deal';
import { StorageStrategyResource } from '@infra/storage';
import { StorageAdapter } from '@infra/storage/storage-adapter';
import { QueryParamsResource } from '@infra/query-params';

export type LandingPageFormData = UTM & Deal;

const ONE_HOUR_IN_MS = 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class LandingPageResource {
  /** this is the form data that comes from landing page */
  private data: LandingPageFormData;

  private cookiesStorage: StorageAdapter;

  constructor(
    private queryParamsResource: QueryParamsResource,
    storageStrategyResource: StorageStrategyResource,
  ) {
    this.cookiesStorage = storageStrategyResource.create('cookies');
  }

  getData() {
    // this is used on staging tests
    this.updateCookiesByEnv();

    try {
      const cookie = this.cookiesStorage.getItem('lp-form');
      this.data = JSON.parse(cookie);
    } catch {}

    if (!this.data) {
      this.clearCookieLpForm();
      return null;
    }

    this.data.computer = ['sim', 'true'].includes(String(this.data.computer).toLowerCase());
    this.data.name = this.data.first_name || this.data.name;
    this.data.order_day = ordersPerDayValues(this.data.order_day);
    this.data.waiter_quantity = waiterQuantityValues(this.data.waiter_quantity);
    this.data.waiter_tables = waiterTablesValues(this.data.waiter_tables);
    this.data.force_billing_core_subscription = String(this.data.force_billing_core_subscription) === "true";
    this.data.force_central_account = String(this.data.force_central_account) === "true";

    this.saveKeysOnCookies(this.data);

    return this.data;
  }

  private saveKeysOnCookies(data: object) {
    // save all data keys into cookie service
    UTM_MODEL_KEYS.forEach(key => {
      const value = data[key as keyof typeof data];

      if (value === undefined) return;
      if (key === 'application') {
        this.cookiesStorage.setItem(key, String(value), new Date(new Date().getTime() + ONE_HOUR_IN_MS));
        return;
      }
      this.cookiesStorage.setItem(key, String(value));
    });

    this.clearCookieLpForm();
  }

  private updateCookiesByEnv() {
    const staging = location.hostname.includes('staging') || location.hostname.includes('localhost');

    if (staging) {
      const formBody = this.queryParamsResource.getCurrent()?.['formBody'];

      if (formBody) {
        this.cookiesStorage.setItem('lp-form', formBody);
      }
    }
  }

  /** clear lp-form key from cookies */
  private clearCookieLpForm() {
    this.cookiesStorage.setItem('lp-form', '', -1);
    this.cookiesStorage.removeItem('lp-form');
  }
}
